import React, { useCallback, useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { tableCellClasses } from "@mui/material/TableCell";
import { styled } from "@mui/material/styles";
import ProductLimit from "../../../components/store/action/ProductLimit";
import MainTitle from "../../../components/headline/MainTitle";
import {
  Button,
  CircularProgress,
  Stack,
  TablePagination,
} from "@mui/material";
import Translation from "../../../utils/Translation";
import AddIcon from "@mui/icons-material/Add";
import UploadIcon from "@mui/icons-material/Upload";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { useNavigate } from "react-router-dom";
import { allLinks, checkValidString } from "../../../utils/helpers";
import TableRows from "./TableRows";
import { useDispatch } from "react-redux";
import axios from "axios";
import { useSelector } from "react-redux";
import {
  loadProductAsync,
  setActiveStep,
  setOffset,
  setPage,
  setProducts,
} from "../../../services/store/storeSlice";
import Grid from "@mui/material/Grid2";
import Container from "@mui/material/Container";
import ProductSearch from "../../../components/store/action/ProductSearch";
import UpdateQuantityModal from "../../../components/store_seller/product/UpdateQuantityModal";

export const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#005d6d",
    color: theme.palette.common.white,
    fontWeight: "bold",
    fontSize: 16,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

export const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

function StoreSeller() {
  const auth = useSelector((state) => state.auth);
  const dynamicState = useSelector((state) => state.dynamicConfig);
  const { language } = useSelector((state) => state.global);
  const {
    limit,
    offset,
    search_term,
    resultSearch,
    products,
    isLoading,
    page,
    statusStoreCrudProduct,
    productIdDeleted,
    product_ids,
    notAttributesLoaded
  } = useSelector((state) => state.store);
  const { sellerInfo } = useSelector((state) => state.seller);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [openQty, setOpenQty] = React.useState(false);
  const [productQty, setProductQty] = useState({});

  const handleChangePage = (event, value) => {
    if (value > 0) {
      dispatch(setOffset(limit * value - limit)); // calculate offset
      dispatch(setPage(value));
    }
  };

  const getProducts = useCallback((source) => {

    let data = {
      limit: limit,
      offset: offset,
      search_term: checkValidString(search_term) ? search_term : "",
      store_ids: sellerInfo?.id,
      cancelToken: source.token,
    };

    // loaded product without load new attributes
    if(notAttributesLoaded)
      data.product_ids = product_ids;

    dispatch(loadProductAsync({ auth, dynamicState, data }));
  });

  useEffect(() => {
    const source = axios.CancelToken.source();
    // check product is empty and reset loading
    if (products.length === 0 && sellerInfo?.id !== undefined)
      getProducts(source);

    return () => {
      source.cancel();
    };
  }, [limit, offset, page, search_term, sellerInfo, dispatch]);

  useEffect(() => {
    if (statusStoreCrudProduct === "succeeded") {
      dispatch(
        setProducts(products.filter((val) => val.id !== productIdDeleted))
      );
    }
  }, [statusStoreCrudProduct, dispatch]);

  const handleAddProduct = () => {
    dispatch(setActiveStep(0));
    navigate(`/${allLinks[16]}`);
  };

  const handleProductImports = () => {
    navigate(`/${allLinks[20]}`);
  };

  const handleManageAddresses = () => {
    // Navigate to manage addresses page - update with correct route
    navigate('/address'); // Update this route as needed
  };

  return (
    <Box>
      <MainTitle title="store_seller.main-title" />

      <Container className="no-padding page_size">
        <Grid container>
          <Grid size={{ lg: 4, md: 5, sm: 6, xs: 12 }}>
            <ProductSearch
              resultSearch={resultSearch}
              search_term={search_term}
              language={language}
              auth={auth}
              dynamicState={dynamicState}
            />
          </Grid>
          <Grid size={{ lg: 3, md: 2, sm: 1 }}></Grid>
          <Grid
            size={{ lg: 1, md: 1, sm: 3, xs: 6 }}
            className="align-content-center limit-responsive"
          >
            <ProductLimit limit={limit} />
          </Grid>
          <Grid
            size={{ lg: 4, md: 4, sm: 2, xs: 6 }}
            className="align-content-center"
          >
            <Stack direction="row-reverse">
              <Button
                variant="contained"
                className="font-weight-600  icon-arabic"
                onClick={handleAddProduct}
                endIcon={<AddIcon />}
              >
                <Translation data="request.add" />
              </Button>
              <Button
                variant="contained"
                className="font-weight-600  icon-arabic"
                onClick={handleManageAddresses}
                endIcon={<LocationOnIcon />}
                sx={{ marginRight: 1 }}
              >
                Manage Addresses
              </Button>
              <Button
                variant="contained"
                className="font-weight-600  icon-arabic"
                onClick={handleProductImports}
                endIcon={<UploadIcon />}
                sx={{ marginRight: 1 }}
              >
                <Translation data="store.manage_imports" />
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Container>

      {isLoading ? (
        <CircularProgress className="full-preloader-position" />
      ) : (
        <>
          {productQty && (
            <UpdateQuantityModal
              product={productQty}
              openQty={openQty}
              setOpenQty={setOpenQty}
            />
          )}

          <Box className="product-table">
            <TableContainer component={Paper}>
              <Table aria-label="collapsible table">
                <TableHead>
                  <StyledTableRow>
                    <StyledTableCell />
                    <StyledTableCell>
                      <Translation data="store_seller.product_id" />{" "}
                    </StyledTableCell>
                    <StyledTableCell>
                      <Translation data="store_seller.product_name" />{" "}
                    </StyledTableCell>
                    <StyledTableCell>
                      <Translation data="store_seller.product_img" />{" "}
                    </StyledTableCell>
                    <StyledTableCell>
                      <Translation data="store_seller.product_review" />{" "}
                    </StyledTableCell>
                    <StyledTableCell>
                      <Translation data="store_seller.status" />{" "}
                    </StyledTableCell>
                    <StyledTableCell>
                      <Translation data="store_seller.rejected_reason" />{" "}
                    </StyledTableCell>
                    <StyledTableCell>
                      <Translation data="store_seller.create_at" />{" "}
                    </StyledTableCell>
                    <StyledTableCell>
                      <Translation data="store_seller.product_action" />
                    </StyledTableCell>
                  </StyledTableRow>
                </TableHead>
                <TableBody>
                  {products.length > 0 &&
                    products.map((row, idx) => (
                      <>
                        <TableRows
                          key={idx}
                          product={row}
                          language={language}
                          auth={auth}
                          dynamicState={dynamicState}
                          setOpenQty={setOpenQty}
                          setProductQty={setProductQty}
                        />
                      </>
                    ))}
                </TableBody>
              </Table>

              <TablePagination
                page={page}
                onPageChange={handleChangePage}
                labelDisplayedRows={({ page }) =>
                  `${Translation({
                    data: "store_seller.rows_per_page",
                  })} ${page}`
                }
                count={-1}
                rowsPerPage={10}
              />
            </TableContainer>
          </Box>
        </>
      )}
    </Box>
  );
}

export default StoreSeller;
